<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue'
import Card from 'primevue/card'
import Divider from 'primevue/divider'
import AlertsTable from '@/components/alerts/AlertsTable.vue'
import { getDexCexProTableColumns } from '@/columns/dexCexColumns'
import { useDexCexAlertsStore } from '@/stores/dexCexAlertsStore'
import { keysToSnake } from '@/utils'
import { useDexCexFilters } from '@/composable/filters/useDexCexFilters'
import type { FormSubmitEvent } from '@primevue/forms'
import AlertsFilters from '@/components/alerts/AlertsFilters.vue'
import { useAuthStore } from '@/stores/authStore'
import InputPopover from '@/components/InputPopover.vue'
import ToggleButton from 'primevue/togglebutton'
import Button from 'primevue/button'
import SrGroupDataTable from '@/components/tables/SrGroupDataTable.vue'
import { useDexAlertsWs } from '@/composable/useDexAlertsWs'
import { useTelegramSignals } from '@/composable/useTgSignals'
import { useDexCexCellFormatter } from '@/composable/formatters/useDexCexCellFormatter'
import { useThemeStore } from '@/stores/themeStore'
import { useRoute } from 'vue-router'
import { useSaveFilters } from '@/composable/useSaveFilters'
import type { DexAlertsFilter, DexAlertSortingKey } from '@/services/alerts-v2-service/types'
import { parseCommaSeparatedAssets } from '@/utils'

// Сторы и композаблы
const alertsStore = useDexCexAlertsStore()
const alertsTable = useTemplateRef<typeof AlertsTable>('alertsTable')
const authStore = useAuthStore()

const route = useRoute()
const isCexSpot = computed(() => (route.meta.cexSpot as boolean))

const tgKey = computed(() => isCexSpot.value ? "enable_alerts_dc_spot" : "enable_alerts_dc")
const { tgSignals } = useTelegramSignals(tgKey.value)

const { start: startWs } = useDexAlertsWs(
  computed(() => alertsStore.filters as DexAlertsFilter),
  computed(() => alertsStore.sortBy as DexAlertSortingKey),
  computed(() => alertsStore.sortOrder == -1 ? 'desc' : 'asc'),
  computed(() => alertsStore.page ?? 0),
  (data) => alertsStore.updateFromWs(data),
)
const themeStore = useThemeStore()
const { saveDexCexFilters } = useSaveFilters()

// Таблица
const tableColumns = getDexCexProTableColumns()
const { formattingRules, updateRules } = useDexCexCellFormatter()

// Работа с фильтрами
const { filters, formData, zodForm, loadFiltersFromSettings } = useDexCexFilters(alertsStore)

// Обработка формы фильтрации
const onFilterFormSubmit = async (e: FormSubmitEvent) => {
  if (!e.valid || !pageInited.value) {
    return
  }
  const snaked = keysToSnake(e.values)
  if (typeof snaked.symbols === 'string') {
    snaked.symbols = parseCommaSeparatedAssets(snaked.symbols)
  }
  alertsStore.filters = {
    ...alertsStore.filters,
    ...snaked,
  }
  if (alertsStore.filters.spread_min === 0) {
    alertsStore.filters.spread_min = null
  }
  try {
    const prom = Promise.all([
      saveDexCexFilters(alertsStore.filters),
      await alertsTable.value?.onPage(null)
    ])
    await prom
  } catch (err) {
    console.error('Failed to save filters:', err)
  }

}

// Прочие переменные
const pageInited = ref(false)

// Жизненный цикл компонента
onMounted(async () => {
  updateRules()
  alertsStore.clear()
  alertsStore.init(isCexSpot.value)
  await authStore.check(true)
  loadFiltersFromSettings(authStore.settings ?? {})
  alertsTable.value?.setSort('spread', -1)
  alertsStore.page = 0
  pageInited.value = true
  startWs()
})

watch(
  () => authStore.currentAccessList,
  (v) => {
    if (!('dex_cex' in v || 'dex_cex_free' in v || 'dex_cex_advanced' in v)) {
      themeStore.forbidden = true
      return
    }
  },
)

const titleKey = computed(() => {
  if (isCexSpot.value) {
    return 'dexCexSpot'
  } {
    return `dexCexSwap`
  }
})

const tgBotLink = computed(() => {
  if (isCexSpot.value) {
    return 'spreadracer_dex_cex_spot_bot'
  } {
    return `spreadracer_dex_bot`
  }
})
</script>

<template>
  <Card class="md:mb-2" pt:body:class="p-0" pt:root:class="rounded-none md:rounded-xl">
    <template #content>
      <AlertsFilters :inited="pageInited" panel-class="px-2 border-none mb-0" :loading="alertsStore.loading"
        :form-submit="onFilterFormSubmit" :form-data="formData" :filters="filters" :zod="zodForm"
        grid-classes="grid-cols-1 gap-3 md:grid-cols-4 " :complexFields="['excludeSelectedSymbols']">
        <template #top-left>
          <span class="pit success font-bold">{{ $t(titleKey) }}</span>
        </template>
        <template #actions>
          <InputPopover icon="pi pi-telegram" severity="secondary" rounded size="small">
            <template #content>
              <div class="grid grid-cols-1 gap-3">
                <ToggleButton size="small" fluid v-model="tgSignals" :offLabel="$t('enableTgSignals')"
                  :onLabel="$t('disableTgSignals')" />
                <Button severity="info" target="_blank" as="a" :href="`https://t.me/${tgBotLink}`" icon="pi pi-telegram"
                  size="small" :label="$t('openTgSignalsBot')"></Button>
              </div>
            </template>
          </InputPopover>
        </template>
      </AlertsFilters>

      <Divider class="mt-0 mb-0"></Divider>
      <AlertsTable :paginator="true" :lazy="true" :formatting-rules="formattingRules" ref="alertsTable"
        :columns="tableColumns" :fake-rows-count="15" :store="alertsStore" :col-full="true"
        :groupSortField="alertsStore.sortBy" :groupSortOrder="alertsStore.sortOrder" :table-component="SrGroupDataTable"
        :table-component-props="{
          groupBy: 'symbol.id',
        }">
      </AlertsTable>
    </template>
  </Card>
</template>
